import { ColumnDef, useModal } from "@commonlibs";
import { GiftCardStockRow } from "common/models/api/models/GiftCardStock";
import { RoleType } from "common/models/role/Role";
import { useNavigate } from "react-router-dom";
import useUserState from "ui/adapter/state/useUserState";
import TableImage from "ui/common/components/display/DataTable/TableImage/TableImage";
import ActionsMenu from "ui/common/components/input/ActionsMenu/ActionsMenu";
import { MenuAction } from "ui/common/components/input/ActionsMenu/ActionsMenu.types";
import { MainRoutes } from "ui/common/router/MainRouter.types";
import useTranslate from "ui/locale/hooks/useTranslate";
import { GiftCardExpireTooltip } from "../containers/components/GiftCardExpireTooltip/GiftCardExpireTooltip";
import DeleteGiftCardStockModal from "../containers/DeleteGiftCardStockModal";

export const GIFTCARD_STOCK_COLUMNS = {
    IMAGE: "imageUrl",
    CODE: "barcode",
    VALUE: "value",
    EXP_DATE: "expirationDate",
    STATUS: "status",
    ACTIONS: "actions",
} as const;

const EXPIRE_THRESHOLD_MONTHS = 2;

export const useGiftCardStockColumns = (): ColumnDef<GiftCardStockRow>[] => {
    const translate = useTranslate();
    const { showModal } = useModal();
    const navigate = useNavigate();
    const { selectedRole } = useUserState();
    const isCustomerService = selectedRole?.roleType === RoleType.CUSTOMER_SERVICE;

    const giftcardStatusMap = {
        AVILABLE: translate("availabled"),
        REDEEMED: translate("redeemed"),
        CANCELLED: translate("cancelled"),
        EXPIRED: translate("expired"),
    };

    return [
        {
            accessorKey: GIFTCARD_STOCK_COLUMNS.IMAGE,
            header: translate("giftCardImage"),
            enableSorting: false,
            size: 50,
            align: "center",
            alignCells: "center",
            cell: (info) => info.getValue(),
            accessorFn: (row) => <TableImage src={row.imageUrl || ""} alt={`${row.name}-image`} />,
        },
        {
            accessorKey: GIFTCARD_STOCK_COLUMNS.CODE,
            header: translate("voucherCode"),
            enableSorting: false,
            size: 120,
            align: "center",
            alignCells: "left",
            accessorFn: (row) => row.barcode,
        },
        {
            accessorKey: GIFTCARD_STOCK_COLUMNS.VALUE,
            header: translate("denomination"),
            enableSorting: false,
            size: 100,
            align: "center",
            alignCells: "center",
            accessorFn: (row) => row.value,
        },
        {
            accessorKey: GIFTCARD_STOCK_COLUMNS.EXP_DATE,
            header: translate("expirationDates"),
            enableSorting: false,
            size: 100,
            align: "center",
            alignCells: "center",
            cell: (info) => info.getValue(),
            accessorFn: (row) => <GiftCardExpireTooltip date={row.expirationDate} month_threshold={EXPIRE_THRESHOLD_MONTHS} />,
        },
        {
            accessorKey: GIFTCARD_STOCK_COLUMNS.STATUS,
            header: translate("status"),
            enableSorting: false,
            size: 100,
            align: "center",
            alignCells: "center",
            accessorFn: (row) => giftcardStatusMap[row.status as keyof typeof giftcardStatusMap] ?? giftcardStatusMap.REDEEMED,
        },
        {
            accessorKey: GIFTCARD_STOCK_COLUMNS.ACTIONS,
            header: () => "",
            size: 60,
            cell: (info) => info.getValue(),
            accessorFn: (row) => {
                const actions: MenuAction[] = [
                    {
                        label: "viewTransactions",
                        onClick: () => navigate(`${MainRoutes.TRANSACTIONS}?address=${row.address}`),
                    },
                ];

                if (!isCustomerService && row.status === "AVILABLE") {
                    actions.push({
                        label: "deleteGiftCard",
                        onClick: () => showModal(DeleteGiftCardStockModal, { giftCard: row }),
                    });
                }

                return <ActionsMenu actions={actions} />;
            },
            enableSorting: false,
        },
    ];
};
